import React, { Fragment, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { buildInvoiceData, renderInvoiceHtml } from "../../utils/invoiceBuilder";

/**
 * "Preview Invoice" + "Print Invoice" for the Dashboard scan list.
 * Read-only: uses the products / wrongScans the Dashboard already built and
 * never changes them. Shows nothing until at least one UPC has been scanned.
 */

/** Print an HTML document through a hidden iframe (no popup blockers). */
const printHtml = (html) => {
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden;";
  document.body.appendChild(iframe);

  const cleanup = () => setTimeout(() => iframe.remove(), 1000);

  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();

  // Give the browser a moment to lay out the document before printing
  setTimeout(() => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.onafterprint = cleanup;
      iframe.contentWindow.print();
    } finally {
      setTimeout(() => iframe.parentNode && iframe.remove(), 60000); // safety net
    }
  }, 250);
};

const InvoiceButtons = ({ products = [], wrongScans = [] }) => {
  const [busy, setBusy] = useState(null); // "preview" | "print" | null
  const [previewHtml, setPreviewHtml] = useState(null);
  const previewFrameRef = useRef(null);

  const { user } = useSelector((state) => state.verifyEmailStore || {});

  const hasScans = products.length > 0 || wrongScans.length > 0;

  // Close preview with Escape
  useEffect(() => {
    if (!previewHtml) return;
    const onKey = (e) => {
      if (e.key === "Escape") setPreviewHtml(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [previewHtml]);

  const makeInvoice = async () => {
    const data = await buildInvoiceData(products, wrongScans);
    return renderInvoiceHtml(data, { preparedBy: user?.name });
  };

  const handlePreview = async () => {
    if (busy) return;
    setBusy("preview");
    try {
      setPreviewHtml(await makeInvoice());
    } catch {
      toast.error("Could not prepare the invoice. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  const handlePrint = async () => {
    if (busy) return;
    setBusy("print");
    try {
      printHtml(await makeInvoice());
    } catch {
      toast.error("Could not prepare the invoice. Please try again.");
    } finally {
      setBusy(null);
    }
  };

  const printFromPreview = () => {
    const win = previewFrameRef.current?.contentWindow;
    if (!win) return;
    win.focus();
    win.print();
  };

  if (!hasScans && !previewHtml) return null;

  const btn =
    "flex items-center gap-2 px-3.5 py-2 rounded-lg text-[13px] font-medium border border-gray-300 " +
    "bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-800 transition-colors disabled:opacity-50 disabled:cursor-wait";

  return (
    <Fragment>
      {hasScans && (
        <div className="flex items-center gap-2 mr-auto">
          <button type="button" className={btn} onClick={handlePreview} disabled={!!busy}>
            <i className={`fa-solid ${busy === "preview" ? "fa-spinner fa-spin" : "fa-eye"}`} />
            Preview Invoice
          </button>
          <button type="button" className={btn} onClick={handlePrint} disabled={!!busy}>
            <i className={`fa-solid ${busy === "print" ? "fa-spinner fa-spin" : "fa-print"}`} />
            Print Invoice
          </button>
        </div>
      )}

      {previewHtml && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4"
          style={{ zIndex: 200000000 }}
          onClick={() => setPreviewHtml(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-[1200px] h-[90vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Invoice preview"
          >
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
              <h3 className="text-[15px] font-bold text-gray-700">
                <i className="fa-solid fa-file-invoice mr-2 text-gray-500" />
                Invoice Preview
              </h3>
              <div className="flex items-center gap-2">
                <button type="button" className={btn} onClick={printFromPreview}>
                  <i className="fa-solid fa-print" /> Print
                </button>
                <button type="button" className={btn} onClick={() => setPreviewHtml(null)}>
                  <i className="fa-solid fa-xmark" /> Close
                </button>
              </div>
            </div>
            <iframe
              ref={previewFrameRef}
              title="Invoice preview"
              srcDoc={previewHtml}
              className="flex-1 w-full border-0 bg-gray-100"
            />
          </div>
        </div>
      )}
    </Fragment>
  );
};

export default InvoiceButtons;